import './formRadioboxList.scss';
import { components } from 'common';
import { lang } from '@umu/ukit-lang';
import { Image } from "@umu/umu-design";
import { getData } from '@umu/umu-util';
import React, { Component } from 'react';
import {
    ESectionValid,
    ESectionType,
    EOptionType,
    ISectionOptionBaseInfo,
    ISectionBaseExtend,
    ISectionBaseSetup,
    ISectionBaseInfo,
    ISectionBaseResult,
} from '@umu/umu-types';

const { Textarea } = components;

export interface IProps<IType, IOption, IExtend> {
    sectionInfo: ISectionBaseInfo<IType, IOption, IExtend>;
    isDisabled?: boolean;
    onValueUpdate(value: ISectionBaseResult<any>, isValid: ESectionValid): void;
}

export interface IState {
    result: ISectionBaseResult<any>;
    isValid: ESectionValid;
}

// tslint:disable-next-line:max-line-length
export default abstract class FormRdioboxList<
    IP extends IProps<ESectionType, ISectionOptionBaseInfo, ISectionBaseExtend<ISectionBaseSetup>>,
    IS extends IState,
> extends Component<IP, IS> {
    public listType: 'radio' | 'checkbox' = 'radio';

    constructor(props: IP, state: IS) {
        super(props, state);

        const { userValue = [], extraValue = '' } = props.sectionInfo;
        const result = {
            userValue,
            extraValue,
            extraId: this.getExtraOptionId(this.props.sectionInfo),
        };
        this.state = {
            result,
            isValid: this.validSection(props.sectionInfo, result),
        } as IS;
    }

    // tslint:disable-next-line:max-line-length
    public abstract getExtraOptionId(
        section: ISectionBaseInfo<any, ISectionOptionBaseInfo, ISectionBaseExtend<ISectionBaseSetup>>,
    ): number;
    // tslint:disable-next-line:max-line-length
    public abstract validSection(
        section: ISectionBaseInfo<any, ISectionOptionBaseInfo, ISectionBaseExtend<ISectionBaseSetup>>,
        result: ISectionBaseResult<any>,
    ): ESectionValid;

    /**
     * @override 获取用户操作的最终值
     */
    public getUserValue() {
        return Object.assign({}, this.state.result);
    }

    /**
     * @override 更新内部状态，向外部传递用户值
     */
    public updateValue(userValue: string[] = []) {
        const result = Object.assign({}, this.state.result, {
            userValue,
        });
        const isValid = this.validSection(this.props.sectionInfo, result);
        this.setState(
            {
                result,
                isValid,
            },
            () => {
                this.props.onValueUpdate(this.getUserValue(), isValid);
            },
        );
    }

    /**
     * @override 更新前valid检查
     */
    public isValidBeforeOnChange(_id: string) {
        return true;
    }

    public handleChange = (e: React.MouseEvent<HTMLDivElement>) => {
        if (this.props.isDisabled) {
            return;
        }

        if ((e.target as HTMLTextAreaElement).nodeName === 'TEXTAREA') {
            return;
        }

        const el = e.currentTarget;
        if (!el) {
            return;
        }

        const id: string = el.getAttribute('data-id') || '';
        if (!id) {
            return;
        }

        if (!this.isValidBeforeOnChange(id)) {
            return;
        }

        this.updateValue([id]);
    };

    public render() {
        return <div className="form-option-list">{this.getOptionList()}</div>;
    }

    public updateExtraValue = (userValue: string = '') => {
        const result = Object.assign({}, this.state.result, {
            extraValue: userValue,
        });
        const isValid = this.validSection(this.props.sectionInfo, result);
        this.setState(
            {
                result,
            },
            () => {
                this.props.onValueUpdate(this.getUserValue(), isValid);
            },
        );
    };

    public renderExtraTextarea(item: ISectionOptionBaseInfo) {
        if (item.type !== EOptionType.OTHER) {
            return null;
        }
        if (!this.isOptionChecked(item.id)) {
            return null;
        }
        let placeholder = lang('（选填）请输入内容');
        if (this.props.sectionInfo.extend.extra_required === 1) {
            placeholder = lang('（必填）请输入内容');
        }
        return (
            <div className="option-extra-textarea">
                <Textarea
                    placeholder={placeholder}
                    maxLength={2000}
                    onChange={this.updateExtraValue}
                    isDisabled={this.props.isDisabled}
                    value={this.state.result.extraValue}
                ></Textarea>
            </div>
        );
    }

    /**
     * @override option checked 逻辑
     */
    public isOptionChecked = (itemId: number) => {
        const { result } = this.state;
        return result.userValue[0] === String(itemId);
    };

    /**
     * @override option 其它类名 逻辑
     */
    public itemClassPredicate = (_item: ISectionOptionBaseInfo) => {
        return '';
    };

    /**
     * @override option 序号渲染逻辑
     */
    public getOptionInput = (_item: ISectionOptionBaseInfo, _index: number) => {
        return (
            <div className="option-input">
                <div className="checkable-box">
                    <div className={`box type-${this.listType}`}></div>
                </div>
            </div>
        );
    };

    public getOptionList = () => {
        const { list, schemeIndex = 1 } = this.props.sectionInfo;

        const ret: JSX.Element[] = [];
        list.map((item, index) => {
            const isChecked = this.isOptionChecked(item.id);
            const clsName = this.itemClassPredicate(item);
            ret.push(
                <div
                    className={`form-option-item scheme-${schemeIndex} ${isChecked ? 'active' : ''} ${clsName}`}
                    key={index}
                    onClick={this.handleChange}
                    data-id={item.id}
                >
                    {this.getOptionInput(item, index)}
                    <div className="option-detail">{item.title}</div>
                    {this.getOptionImg(item)}
                    {this.renderExtraTextarea(item)}
                </div>,
            );
        });
        return ret;
    };

    public getOptionImg = (item: ISectionOptionBaseInfo) => {
        const { resource_arr = [] } = item.extend;
        const resourceInfo = resource_arr[0];
        if (!resourceInfo) {
            return null;
        }

        let url = getData(resourceInfo, 'resource_data', 'url');
        // url = ImageUtil.appendWidth(url, 600);
        // 兼容 url 返回数组的情况
        if (Array.isArray(url)) {
            url = url[0];
        }
        return (
            <div className="option-image">
                <div className="img">
                    <Image src={url} previewControl={true} onlyClickControl={true}/>
                </div>
            </div>
        );
    };
}
